<script lang="ts">
    import { toast } from 'svelte-sonner';
    import { useForm, page } from '@inertiajs/svelte';
    import Modal from '@/components/Modal.svelte';
    import FormContainer from '@/components/Form/FormContainer.svelte';
    import FormBlock from '@/components/Form/FormBlock.svelte';
    import { Button } from '@/components/ui/button';
    import Input from '@/components/Form/Inputs/Input.svelte';
    import Textarea from '@/components/Inputs/Textarea.svelte';
    import DateTime from '@/components/Inputs/DateTime.svelte';
    import Checkbox from '@/components/Inputs/Checkbox.svelte';
    import InlineEditable from '@/components/Inputs/InlineEditable.svelte';
    import { router } from '@inertiajs/svelte';
    import { Check, X, AlertTriangle } from 'lucide-svelte';
    import Gallery from '@/components/Gallery.svelte';
    import type Blocks from '@/schemas/public/Blocks';
    import type { Field } from '@/types/types';
    import FilePond, { registerPlugin } from 'svelte-filepond';
    import FilePondPluginFileValidateSize from 'filepond-plugin-file-validate-size';
    import 'filepond/dist/filepond.min.css';
    import { uploadImages } from '@/lib/imageUpload';

    registerPlugin(FilePondPluginFileValidateSize);

    type HarnessImage = {
        id: string;
        thumbnail_url: string;
        full_url: string;
    };

    type Inspection = {
        id: string;
        inspection_date: string;
        fit_for_use: boolean;
        defects_found: string | null;
        inspector_name: string | null;
        images?: HarnessImage[];
    };

    type Harness = {
        id: string;
        harness_number: string;
        lanyard_number: string | null;
        purchase_date: string | null;
        expiry_date: string | null;
        manufacturer: string | null;
        model: string | null;
        notes: string | null;
        latest_inspection: Inspection | null;
        inspections?: Inspection[];
        images?: HarnessImage[];
    };

    type Props = {
        harness: Harness | null;
        show: boolean;
        inspectionBlocks: (Blocks & { fields: Field[] })[];
        harnessBlocks?: (Blocks & { fields: Field[] })[];
    };

    let { harness, show = $bindable(), inspectionBlocks, harnessBlocks = [] }: Props = $props();

    let inspectionPondFiles = $state<any[]>([]);
    let pendingInspectionImages = $state<File[]>([]);

    function handleInspectionAddFile(err: any, fileItem: any) {
        if (!err) {
            pendingInspectionImages = [...pendingInspectionImages, fileItem.file];
        }
    }

    function handleInspectionRemoveFile(err: any, fileItem: any) {
        if (!err) {
            pendingInspectionImages = pendingInspectionImages.filter(f => f !== fileItem.file);
        }
    }

    function close() {
        show = false;
        inspectionPondFiles = [];
        pendingInspectionImages = [];
    }

    // Build harness fields with values for InlineEditable
    let harnessFieldsWithValues = $derived.by(() => {
        if (!harness || !harnessBlocks.length) return [];

        return harnessBlocks.flatMap(block =>
            block.fields.map(field => ({
                ...field,
                value: harness[field.column_name as keyof Harness] ?? null,
            }))
        );
    });

    // Combine harness images + all inspection images with date badges
    let allHarnessImages = $derived.by(() => {
        const harnessImgs = (harness?.images ?? []).map(img => ({
            ...img,
            source: 'Harness',
        }));
        const inspectionImgs = (harness?.inspections ?? []).flatMap(inspection => {
            const [y, m, d] = (inspection.inspection_date ?? '').split('-');
            const formatted = d && m && y ? `${d}-${m}-${y}` : inspection.inspection_date;
            return (inspection.images ?? []).map(img => ({
                ...img,
                source: formatted,
            }));
        });
        return [...harnessImgs, ...inspectionImgs];
    });

    let editingFieldId = $state<number | undefined>(undefined);

    // Inline form for harness updates
    let inlineFormInner: Record<string, any> = {};
    $effect(() => {
        if (harness && harnessBlocks.length) {
            for (const block of harnessBlocks) {
                for (const field of block.fields) {
                    inlineFormInner[field.column_name] = harness[field.column_name as keyof Harness] ?? null;
                }
            }
        }
    });
    let inlineForm = $state(useForm(inlineFormInner));

    function saveHarnessField(columnName: string, value: any) {
        if (!harness) return;

        $inlineForm[columnName] = value;
        router.put(route('harnesses.update', { harness: harness.id }), $inlineForm.data(), {
            preserveScroll: true,
            onSuccess: () => {
                editingFieldId = undefined;
                toast.success('Harness updated');
            },
            onError: (errors) => {
                for (const message of Object.values(errors)) {
                    toast.error(message as string);
                }
            }
        });
    }

    // Initialize inspection form from blocks
    let inspectionFormInner: Record<string, any> = {};
    for (const block of inspectionBlocks) {
        for (const field of block.fields) {
            if (field.ui_type === 'checkbox') {
                inspectionFormInner[field.column_name] = true;
            } else if (field.column_name === 'inspection_date') {
                inspectionFormInner[field.column_name] = new Date().toISOString().split('T')[0];
            } else {
                inspectionFormInner[field.column_name] = null;
            }
        }
    }

    let inspectionForm = $state(useForm(inspectionFormInner));

    function submitInspection(e: Event) {
        e.preventDefault();
        if (!harness) return;

        $inspectionForm.processing = true;

        router.post(route('harnesses.inspections.store', { harness: harness.id }), $inspectionForm.data(), {
            preserveScroll: true,
            onSuccess: async (pageData: any) => {
                const inspectionId = pageData.props?.flash?.inspectionId;

                if (pendingInspectionImages.length > 0 && inspectionId) {
                    try {
                        const csrfToken = $page.props.csrfToken || '';
                        await uploadImages(
                            pendingInspectionImages,
                            'App\\Models\\HarnessInspection',
                            inspectionId,
                            csrfToken
                        );
                    } catch (error) {
                        console.error('Error uploading inspection images:', error);
                        toast.error('Inspection recorded but some images failed to upload');
                    }
                }

                toast.success('Inspection recorded successfully!');
                $inspectionForm.reset();
                // Reset to defaults
                for (const block of inspectionBlocks) {
                    for (const field of block.fields) {
                        if (field.ui_type === 'checkbox') {
                            $inspectionForm[field.column_name] = true;
                        } else if (field.column_name === 'inspection_date') {
                            $inspectionForm[field.column_name] = new Date().toISOString().split('T')[0];
                        } else {
                            $inspectionForm[field.column_name] = null;
                        }
                    }
                }
                close();
            },
            onError: (errors) => {
                for (const message of Object.values(errors)) {
                    toast.error(`Error recording inspection: ${message as string}`);
                }
                $inspectionForm.processing = false;
            },
            onFinish: () => {
                $inspectionForm.processing = false;
            },
        });
    }
</script>

{#if show && harness}
    <Modal title="Harness Details - {harness.harness_number}" onClose={close}>
        <div class="pt-2">
            <!-- Harness Info with InlineEditable -->
            <FormContainer>
                <FormBlock label="Harness Information">
                    {#each harnessFieldsWithValues as field (field.id)}
                        <InlineEditable
                            {field}
                            bind:editingFieldId={editingFieldId}
                            error={$inlineForm.errors[field.column_name]}
                            onSave={(newValue) => saveHarnessField(field.column_name, newValue)}
                            record={harness}
                        />
                    {/each}
                </FormBlock>

                {#if allHarnessImages.length > 0}
                    <Gallery
                        images={allHarnessImages}
                        relatedRecordType="App\Models\Harness"
                        relatedRecordId={harness.id}
                        showSourceBadge={true}
                    />
                {/if}
            </FormContainer>

            <!-- Latest Inspection Status -->
            <div class="rounded-lg border p-4 mb-4 mt-4">
                <h4 class="text-sm font-medium mb-2">Latest Inspection</h4>
                {#if harness.latest_inspection}
                    <div class="flex items-center gap-4">
                        <div class="flex items-center gap-2">
                            {#if harness.latest_inspection.fit_for_use}
                                <div class="flex items-center gap-1 text-green-600">
                                    <Check class="h-5 w-5" />
                                    <span>Fit for Use</span>
                                </div>
                            {:else}
                                <div class="flex items-center gap-1 text-red-600">
                                    <X class="h-5 w-5" />
                                    <span>Defects Found</span>
                                </div>
                            {/if}
                        </div>
                        <span class="text-muted-foreground text-sm">
                            on {harness.latest_inspection.inspection_date}
                            {#if harness.latest_inspection.inspector_name}
                                by {harness.latest_inspection.inspector_name}
                            {/if}
                        </span>
                    </div>
                    {#if harness.latest_inspection.defects_found}
                        <p class="mt-2 text-sm text-muted-foreground">
                            {harness.latest_inspection.defects_found}
                        </p>
                    {/if}
                {:else}
                    <div class="flex items-center gap-2 text-amber-600">
                        <AlertTriangle class="h-5 w-5" />
                        <span>No inspections recorded</span>
                    </div>
                {/if}
            </div>

            <!-- New Inspection Form -->
            <FormContainer>
                <form onsubmit={submitInspection}>
                    {#each inspectionBlocks as block (block.id)}
                        <FormBlock label="Record New Inspection">
                            {#each block.fields as field (field.id)}
                                {#if field.editable !== false}
                                    {#if field.ui_type === 'checkbox'}
                                        <Checkbox
                                            bind:checked={$inspectionForm[field.column_name]}
                                            label={field.label}
                                            error={$inspectionForm.errors[field.column_name]}
                                        />
                                    {:else if field.ui_type === 'textarea'}
                                        <Textarea
                                            label={field.label}
                                            bind:value={$inspectionForm[field.column_name]}
                                            error={$inspectionForm.errors[field.column_name]}
                                            {field}
                                        />
                                    {:else if field.ui_type === 'datetime'}
                                        <DateTime
                                            label={field.label}
                                            bind:value={$inspectionForm[field.column_name]}
                                            error={$inspectionForm.errors[field.column_name]}
                                            {field}
                                        />
                                    {:else}
                                        <Input
                                            {field}
                                            bind:value={$inspectionForm[field.column_name]}
                                            error={$inspectionForm.errors[field.column_name]}
                                            onkeydown={() => {}}
                                        />
                                    {/if}
                                {/if}
                            {/each}
                            <div class="col-span-full">
                                <p class="text-sm text-muted-foreground mb-2">
                                    Upload photos from the inspection
                                </p>
                                <FilePond
                                    bind:files={inspectionPondFiles}
                                    server={null}
                                    allowMultiple={true}
                                    maxFiles={10}
                                    maxFileSize="30MB"
                                    labelMaxFileSizeExceeded="File is too large"
                                    labelMaxFileSize={"Maximum file size is {filesize}"}
                                    onwarning={(error) => {
                                        if (error.body) toast.error(error.body);
                                    }}
                                    name="images"
                                    acceptedFileTypes={['image/*']}
                                    labelIdle='Drag & Drop your images or <span class="filepond--label-action">Browse</span>'
                                    onaddfile={handleInspectionAddFile}
                                    onremovefile={handleInspectionRemoveFile}
                                />
                            </div>
                        </FormBlock>
                    {/each}

                    <div class="mt-5 flex justify-end gap-2">
                        <Button type="button" onclick={close}>Cancel</Button>
                        <Button type="submit" disabled={$inspectionForm.processing}>
                            {#if $inspectionForm.processing}Recording...{:else}Record Inspection{/if}
                        </Button>
                    </div>
                </form>
            </FormContainer>

            <!-- Previous Inspections -->
            {#if harness.inspections && harness.inspections.length > 0}
                <div>
                    <h4 class="text-sm font-medium mb-3">Inspection History</h4>
                    <div class="space-y-2 max-h-48 overflow-y-auto">
                        {#each harness.inspections as inspection (inspection.id)}
                            <div class="flex items-center justify-between p-3 rounded-lg border text-sm">
                                <div class="flex items-center gap-3">
                                    {#if inspection.fit_for_use}
                                        <Check class="h-4 w-4 text-green-600" />
                                    {:else}
                                        <X class="h-4 w-4 text-red-600" />
                                    {/if}
                                    <span>{inspection.inspection_date}</span>
                                    {#if inspection.inspector_name}
                                        <span class="text-muted-foreground">by {inspection.inspector_name}</span>
                                    {/if}
                                </div>
                                {#if inspection.defects_found}
                                    <span class="text-muted-foreground truncate max-w-[200px]" title={inspection.defects_found}>
                                        {inspection.defects_found}
                                    </span>
                                {:else}
                                    <span class="text-muted-foreground">No defects</span>
                                {/if}
                            </div>
                        {/each}
                    </div>
                </div>
            {/if}
        </div>
    </Modal>
{/if}
